// useLdBatchIngest — bulk intake at engagement scale (Asana 1216373781992284 item 1).
//
// Uploads a zip and/or a multi-file selection (including a folder drop, which the browser
// flattens to a plain FileList) to POST /api/logic-decomposer/ingest-batch, then polls
// GET /ingest-batch/:batchId for progress. Unlike the per-file sequential client loop
// (useArtifactAnalyze's `ingest`, driven one HTTP round-trip at a time from the browser), the
// sequential work happens SERVER-SIDE here: the batch keeps running if the tab is closed or the
// page is reloaded, and the client just resumes polling the same batch id.
//
// The batch id is persisted to sessionStorage per project so a reload during a 50-200 file run
// doesn't lose track of it — the dashboard mounts, sees a saved id, and resumes polling instead
// of starting over.

import { useCallback, useRef, useState } from 'react';
import { apiClient } from '../../api';

export interface LdBatchFileStatus {
  name: string;
  status: 'queued' | 'running' | 'done' | 'error';
  run_id: string | null;
  error: string | null;
}

export interface LdBatchStatus {
  batch_id: string;
  status: 'running' | 'done' | 'error';
  total: number;
  done: number;
  skipped: { name: string; reason: string }[];
  files: LdBatchFileStatus[];
  error: string | null;
  elapsed_ms: number;
}

const POLL_INTERVAL_MS = 3000;

const storageKey = (projectId: number) => `ld_batch_id_project_${projectId}`;

export function savedBatchId(projectId: number): string | null {
  if (typeof window === 'undefined') return null;
  try { return window.sessionStorage.getItem(storageKey(projectId)); } catch { return null; }
}

function saveBatchId(projectId: number, batchId: string | null) {
  if (typeof window === 'undefined') return;
  try {
    if (batchId) window.sessionStorage.setItem(storageKey(projectId), batchId);
    else window.sessionStorage.removeItem(storageKey(projectId));
  } catch { /* sessionStorage unavailable — batch still works, just isn't resumable across a reload */ }
}

export interface UseLdBatchIngest {
  batch: LdBatchStatus | null;
  starting: boolean;
  /** File count for the upload currently in flight (0 when `starting` is false). QA finding
   *  2026-08-03: a 50-200 file folder upload could take many seconds to finish the initial
   *  POST, and the UI showed nothing at all in that window — this is what a loading state
   *  reads to say "Uploading 137 files…" instead of going blank. */
  startingCount: number;
  error: string | null;
  /** Start a bulk run: any mix of individual files and/or a single zip archive. */
  start: (opts: { files?: File[]; archive?: File; projectId: number }) => Promise<void>;
  /** Resume polling a batch that was already running (e.g. after a page reload). */
  resume: (batchId: string, projectId: number) => void;
  dismiss: () => void;
}

export function useLdBatchIngest(): UseLdBatchIngest {
  const [batch, setBatch] = useState<LdBatchStatus | null>(null);
  const [starting, setStarting] = useState(false);
  const [startingCount, setStartingCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentProjectId = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
  }, []);

  const poll = useCallback((batchId: string, projectId: number) => {
    stopPolling();
    const tick = async () => {
      try {
        const res = await apiClient.get(`/api/logic-decomposer/ingest-batch/${batchId}`);
        const b = res.data as LdBatchStatus;
        setBatch(b);
        if (b.status === 'running') {
          pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
        } else {
          saveBatchId(projectId, null);
        }
      } catch (e: any) {
        // A transient network blip shouldn't kill a 50-200 file run — keep polling. A real 404
        // (batch expired after the TTL) surfaces as an error status and stops.
        if (e?.response?.status === 404) {
          setError('That batch has expired.');
          saveBatchId(projectId, null);
          return;
        }
        pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    tick();
  }, [stopPolling]);

  const start = useCallback(async ({ files = [], archive, projectId }: { files?: File[]; archive?: File; projectId: number }) => {
    if (files.length === 0 && !archive) return;
    setStarting(true);
    setStartingCount(files.length + (archive ? 1 : 0));
    setError(null);
    currentProjectId.current = projectId;
    try {
      const form = new FormData();
      files.forEach((f) => form.append('files', f, (f as any).webkitRelativePath || f.name));
      if (archive) form.append('archive', archive, archive.name);
      form.append('projectId', String(projectId));
      const res = await apiClient.post('/api/logic-decomposer/ingest-batch', form);
      const batchId = (res.data as { batch_id?: string }).batch_id;
      if (!batchId) throw new Error('The server did not start a batch.');
      saveBatchId(projectId, batchId);
      setBatch(res.data as LdBatchStatus);
      poll(batchId, projectId);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not start the batch.');
    } finally {
      setStarting(false);
      setStartingCount(0);
    }
  }, [poll]);

  const resume = useCallback((batchId: string, projectId: number) => {
    currentProjectId.current = projectId;
    poll(batchId, projectId);
  }, [poll]);

  const dismiss = useCallback(() => {
    stopPolling();
    if (currentProjectId.current != null) saveBatchId(currentProjectId.current, null);
    setBatch(null);
    setError(null);
  }, [stopPolling]);

  return { batch, starting, startingCount, error, start, resume, dismiss };
}
